import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Checkbox } from 'antd';
import { QuestionCircleOutlined, HomeOutlined, LineChartOutlined, UserOutlined } from '@ant-design/icons';
import { useLocation } from 'react-router-dom';
import './TourPrompt.pcss';

interface TourPromptProps {
    visible: boolean;
    onStartTour: () => void;
    onSkipTour: (rememberChoice: boolean) => void;
}

export const TourPrompt: React.FC<TourPromptProps> = ({ visible, onStartTour, onSkipTour }) => {
    const location = useLocation();
    const [rememberChoice, setRememberChoice] = useState(false);

    const handleSkip = useCallback(() => {
        onSkipTour(rememberChoice);
    }, [onSkipTour, rememberChoice]);

    const handleStart = useCallback(() => {
        onStartTour();
    }, [onStartTour]);

    // Keyboard event handler for tour prompt
    useEffect(() => {
        if (!visible) return;
        
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Enter') {
                event.preventDefault();
                event.stopPropagation();
                handleStart(); // Enter = Start tour
            } else if (event.key === 'Escape') {
                event.preventDefault();
                event.stopPropagation();
                handleSkip(); // Escape = Skip tour
            }
        };
        
        // Add event listener
        document.addEventListener('keydown', handleKeyDown, true);
        
        // Cleanup
        return () => {
            document.removeEventListener('keydown', handleKeyDown, true);
        };
    }, [visible, rememberChoice, handleStart, handleSkip]);

    // Get custom content based on current path
    const getModalContent = () => {
        if (location.pathname === '/') {
            return {
                icon: <HomeOutlined style={{ color: '#52c41a', fontSize: '24px' }} />,
                title: 'Welcome to Our Trading Platform!',
                description: 'Ready to explore? We\'ll show you around the main navigation, trading features, and all the tools you need to get started.',
                highlights: [
                    '🚀 Quick navigation tour',
                    '📈 Trading features overview', 
                    '🏆 Contest & competitions',
                    '❓ Q&A and support resources'
                ],
                className: 'tour-prompt-homepage'
            };
        }
        
        if (location.pathname.includes('/priceboard')) {
            return {
                icon: <LineChartOutlined style={{ color: '#1890ff', fontSize: '24px' }} />,
                title: 'Welcome to Trading Dashboard!',
                description: 'This is your comprehensive trading workspace. Let us show you how to search stocks, analyze data, and execute trades efficiently.',
                highlights: [
                    '🔍 Advanced stock search',
                    '📊 Real-time price data',
                    '📈 Market group filters',
                    '💰 Quick order placement'
                ],
                className: 'tour-prompt-trading'
            };
        }
        
        if (location.pathname.includes('/profile')) {
            return {
                icon: <UserOutlined style={{ color: '#722ed1', fontSize: '24px' }} />,
                title: 'Welcome to Your Profile!',
                description: 'Your personal dashboard is packed with features. We\'ll guide you through managing your assets, viewing orders, and customizing your experience.',
                highlights: [
                    '💼 Portfolio management',
                    '💰 Asset overview',
                    '📋 Order history',
                    '⚙️ Account settings'
                ],
                className: 'tour-prompt-profile'
            };
        }

        // Default fallback
        return {
            icon: <QuestionCircleOutlined style={{ color: '#1890ff', fontSize: '24px' }} />,
            title: 'Platform Tour',
            description: 'Would you like to take a quick tour to learn about the features available on this page?',
            highlights: [],
            className: 'tour-prompt-default'
        };
    };

    const modalContent = getModalContent();
    
    return (
        <Modal
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {modalContent.icon}
                    <span style={{ fontSize: '18px', fontWeight: '600' }}>{modalContent.title}</span>
                </div>
            }
            open={visible}
            footer={null}
            closable={false}
            centered
            width={520}
            className={`tour-prompt-modal ${modalContent.className}`}
        >
            <div style={{ padding: '20px 0' }}>
                <p style={{ marginBottom: '24px', fontSize: '16px', lineHeight: '1.6' }}>
                    {modalContent.description}
                </p>
                

                
                <div style={{ marginBottom: '24px' }}>
                    <Checkbox 
                        checked={rememberChoice} 
                        onChange={(e) => setRememberChoice(e.target.checked)}
                        style={{ color: 'white' }}
                    >
                        <span style={{ color: 'white' }}>Don't ask me again</span>
                    </Checkbox>
                </div>
                
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                    <Button onClick={handleSkip} size="large">
                        {rememberChoice ? 'Skip Forever' : 'Skip for Now'}
                    </Button>
                    <Button type="primary" onClick={handleStart} size="large">
                        Yes, Start Tour
                    </Button>
                </div>
            </div>
        </Modal>
    );
};
